import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const PRIORITIES = ['Low','Medium','High','Critical'];
const ACTIVE_STATUSES = ['Open','In Progress'];
const RESOLVED_OPTIONS = [
  'Resolved',
  'Resolved - Vendor Case',
  'Resolved - 3 Remainders',
  'Resolved - Known Issues',
  'Resolved - Feature Request',
  'Resolved - Workaround'
];
const isResolved = s => RESOLVED_OPTIONS.includes(s);

const emptyCall = () => ({ rd_start_dt:'', rd_end_dt:'', call_start_dt:'', call_end_dt:'', call_notes:'' });

// Resolve modal
function ResolveModal({ onSelect, onCancel, disabled }) {
  return (
    <div className="modal-backdrop" onClick={e => e.target===e.currentTarget && onCancel()}>
      <div className="modal" style={{maxWidth:420}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
          <div style={{width:36,height:36,borderRadius:'50%',background:'#EAF3DE',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <i className="fa-solid fa-circle-check" style={{color:'#3B6D11',fontSize:16}}></i>
          </div>
          <div style={{fontSize:15,fontWeight:600}}>Mark as Resolved</div>
        </div>
        <p style={{fontSize:13,color:'#555',marginBottom:16}}>Select the resolution type:</p>
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {RESOLVED_OPTIONS.map(opt => (
            <button key={opt} type="button"
              onClick={() => onSelect(opt)}
              style={{
                textAlign:'left',padding:'10px 14px',borderRadius:8,
                border:'1px solid #e8e8f0',background:'#fff',cursor:'pointer',
                fontSize:13,color:'#333',fontFamily:'inherit',
                transition:'all 0.15s'
              }}
              onMouseEnter={e=>{e.target.style.background='#EAF3DE';e.target.style.borderColor='#97C459';}}
              onMouseLeave={e=>{e.target.style.background='#fff';e.target.style.borderColor='#e8e8f0';}}>
              <i className="fa-solid fa-check" style={{color:'#5DCAA5',marginRight:8}}></i>
              {opt}
            </button>
          ))}
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',marginTop:16}}>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function TicketForm({ initial, calls: initCalls=[], onSubmit, loading, error, success, isEdit=false }) {
  const { user, isAdmin } = useAuth();
  const [lookups, setLookups]       = useState({});
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [form, setForm] = useState(initial || {
    opened_on:'', opened_by_name:'', opened_by_email:'',
    support_plan_id:'', product_type_id:'', platform_id:'',
    ats_consultant_id:'', sales_manager_id:'', client_id:'',
    priority:'Medium', status:'Open',
    issue_short_desc:'', issue_detail_desc:'',
    classification_id:'', category_id:'',
    operating_system:'', product_version:'',
    gtl_responded_on:'', latest_update_note:'',
    resolution_desc:'', remarks:''
  });
  const [calls, setCalls] = useState(initCalls.length ? initCalls : [emptyCall()]);

  useEffect(() => {
    api.get('/lookups/all').then(({ data }) => {
      setLookups(data);
      if (!isAdmin && !initial?.ats_consultant_id) {
        const match = (data.ats_consultants||[]).find(
          c => c.name.trim().toLowerCase() === (user?.full_name||'').trim().toLowerCase()
        );
        setForm(f => ({ ...f, ats_consultant_id: match ? String(match.id) : f.ats_consultant_id }));
      }
    }).catch(()=>{});
  }, []);

  const set = (k,v) => setForm(f => ({...f,[k]:v}));
  const setCall    = (i,k,v) => setCalls(cs => cs.map((c,idx) => idx===i ? {...c,[k]:v} : c));
  const addCall    = () => setCalls(cs => [...cs, emptyCall()]);
  const removeCall = i  => setCalls(cs => cs.filter((_,idx) => idx!==i));

  const handleSubmit = e => { e.preventDefault(); onSubmit(form, calls); };
  const lu = table => lookups[table] || [];

  const consultantName = lu('ats_consultants').find(
    c => String(c.id) === String(form.ats_consultant_id)
  )?.name || user?.full_name || '';

  const openedOnLocked = isEdit && !isAdmin;

  // Resolve button enabled only when both fields filled
  const canResolve = form.resolution_desc?.trim().length > 0 && form.issue_detail_desc?.trim().length > 0;
  const currentlyResolved = isResolved(form.status);

  const handleResolveSelect = (opt) => {
    set('status', opt);
    setShowResolveModal(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      {showResolveModal && (
        <ResolveModal
          onSelect={handleResolveSelect}
          onCancel={() => setShowResolveModal(false)}
        />
      )}

      {error   && <div className="alert alert-error"><i className="fa-solid fa-circle-exclamation"></i> {error}</div>}
      {success && <div className="alert alert-success"><i className="fa-solid fa-circle-check"></i> {success}</div>}

      {/* ── Ticket Information ── */}
      <div className="card">
        <div className="section-divider"><i className="fa-solid fa-circle-info"></i> Ticket Information</div>
        <div className="form-grid">
          <div className="field">
            <label>
              Opened On <span className="req">*</span>
              {openedOnLocked && <span style={{marginLeft:6,fontSize:10,color:'#aaa',fontWeight:400}}><i className="fa-solid fa-lock" style={{fontSize:9}}></i> admin only</span>}
            </label>
            <input type="datetime-local" value={form.opened_on}
              onChange={e => !openedOnLocked && set('opened_on', e.target.value)}
              readOnly={openedOnLocked} required
              style={openedOnLocked?{background:'#f8f9ff',color:'#555',cursor:'not-allowed'}:{}} />
          </div>
          <div></div>
          <div className="field">
            <label>Opened By (Name) <span className="req">*</span></label>
            <input type="text" value={form.opened_by_name}
              onChange={e=>set('opened_by_name',e.target.value)} placeholder="Enter name" required />
          </div>
          <div className="field">
            <label>Opened By (Email) <span className="req">*</span></label>
            <input type="email" value={form.opened_by_email}
              onChange={e=>set('opened_by_email',e.target.value)} placeholder="email@example.com" required />
          </div>
          <div className="field">
            <label>Client <span className="req">*</span></label>
            <select value={form.client_id} onChange={e=>set('client_id',e.target.value)} required>
              <option value="">Select client...</option>
              {lu('clients').map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Support Plan <span className="req">*</span></label>
            <select value={form.support_plan_id} onChange={e=>set('support_plan_id',e.target.value)} required>
              <option value="">Select plan...</option>
              {lu('support_plans').map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>
              ATS Consultant <span className="req">*</span>
              {!isAdmin && <span style={{marginLeft:6,fontSize:10,color:'#aaa',fontWeight:400}}><i className="fa-solid fa-lock" style={{fontSize:9}}></i> auto</span>}
            </label>
            {isAdmin ? (
              <select value={form.ats_consultant_id} onChange={e=>set('ats_consultant_id',e.target.value)} required>
                <option value="">Select consultant...</option>
                {lu('ats_consultants').map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            ) : (
              <>
                <input type="text" value={consultantName} readOnly style={{background:'#f8f9ff',color:'#555',cursor:'not-allowed'}}/>
                <input type="hidden" value={form.ats_consultant_id}/>
              </>
            )}
          </div>
          <div className="field">
            <label>Sales Manager <span className="req">*</span></label>
            <select value={form.sales_manager_id} onChange={e=>set('sales_manager_id',e.target.value)} required>
              <option value="">Select manager...</option>
              {lu('sales_managers').map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── Product & Platform ── */}
      <div className="card">
        <div className="section-divider"><i className="fa-solid fa-laptop"></i> Product & Platform</div>
        <div className="form-grid-3">
          <div className="field">
            <label>Product Type <span className="req">*</span></label>
            <select value={form.product_type_id} onChange={e=>set('product_type_id',e.target.value)} required>
              <option value="">Select product...</option>
              {lu('product_types').map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Product Version</label>
            <input type="text" value={form.product_version} onChange={e=>set('product_version',e.target.value)} placeholder="e.g. v4.2.1"/>
          </div>
          <div className="field">
            <label>Platform <span className="req">*</span></label>
            <select value={form.platform_id} onChange={e=>set('platform_id',e.target.value)} required>
              <option value="">Select platform...</option>
              {lu('platforms').map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Operating System</label>
            <input type="text" value={form.operating_system} onChange={e=>set('operating_system',e.target.value)} placeholder="e.g. Windows Server 2022"/>
          </div>
          <div className="field">
            <label>Classification <span className="req">*</span></label>
            <select value={form.classification_id} onChange={e=>set('classification_id',e.target.value)} required>
              <option value="">Select...</option>
              {lu('classifications').map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Category <span className="req">*</span></label>
            <select value={form.category_id} onChange={e=>set('category_id',e.target.value)} required>
              <option value="">Select...</option>
              {lu('categories').map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── Issue Details ── */}
      <div className="card">
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
          <div className="section-divider" style={{margin:0,flex:1}}><i className="fa-solid fa-file-lines"></i> Issue Details</div>
          {/* Resolve button — top right, only in edit mode */}
          {isEdit && !currentlyResolved && (
            <div style={{marginLeft:16,flexShrink:0}}>
              <button type="button"
                onClick={() => canResolve && setShowResolveModal(true)}
                title={!canResolve ? 'Fill in Issue Detail Description and Resolution Description first' : 'Mark as Resolved'}
                style={{
                  display:'flex',alignItems:'center',gap:7,
                  padding:'7px 14px',borderRadius:7,border:'none',cursor: canResolve ? 'pointer' : 'not-allowed',
                  background: canResolve ? '#5DCAA5' : '#e0e0e0',
                  color: canResolve ? '#fff' : '#aaa',
                  fontSize:13,fontWeight:500,fontFamily:'inherit',
                  transition:'all 0.2s'
                }}>
                <i className="fa-solid fa-circle-check"></i>
                Mark Resolved
                {!canResolve && <i className="fa-solid fa-lock" style={{fontSize:10,marginLeft:2}}></i>}
              </button>
              {!canResolve && (
                <div style={{fontSize:11,color:'#aaa',marginTop:4,textAlign:'right'}}>
                  Fill Issue Detail + Resolution Description
                </div>
              )}
            </div>
          )}
          {/* Show resolved badge if already resolved */}
          {currentlyResolved && (
            <span style={{marginLeft:16,background:'#EAF3DE',color:'#3B6D11',padding:'5px 12px',borderRadius:99,fontSize:12,fontWeight:500}}>
              <i className="fa-solid fa-circle-check" style={{marginRight:5}}></i>{form.status}
            </span>
          )}
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div className="field">
            <label>Issue Short Description <span className="req">*</span></label>
            <input type="text" value={form.issue_short_desc}
              onChange={e=>set('issue_short_desc',e.target.value)} placeholder="One-line summary..." required/>
          </div>
          <div className="field">
            <label>Priority <span className="req">*</span></label>
            <div className="pri-btns">
              {PRIORITIES.map(p=>(
                <button type="button" key={p}
                  className={`pri-btn ${p.toLowerCase()} ${form.priority===p?'sel':''}`}
                  onClick={()=>set('priority',p)}>{p}</button>
              ))}
            </div>
          </div>

          {/* Status — only Open/In Progress shown as buttons, no Resolved here */}
          <div className="field">
            <label>Status</label>
            <div className="status-btns">
              {ACTIVE_STATUSES.map(s=>(
                <button type="button" key={s}
                  className={`status-btn ${form.status===s?'sel':''}`}
                  onClick={()=>!currentlyResolved && set('status',s)}
                  style={currentlyResolved?{opacity:0.5,cursor:'not-allowed'}:{}}>
                  {s}
                </button>
              ))}
              {/* Admin can revert resolved ticket to In Progress */}
              {isAdmin && currentlyResolved && (
                <button type="button"
                  onClick={() => set('status','In Progress')}
                  style={{
                    padding:'6px 14px',fontSize:12,fontWeight:500,borderRadius:99,
                    border:'1px solid #EF9F27',cursor:'pointer',
                    background:'#FAEEDA',color:'#854F0B',fontFamily:'inherit'
                  }}>
                  <i className="fa-solid fa-rotate-left" style={{marginRight:5}}></i>
                  Revert to In Progress
                </button>
              )}
            </div>
          </div>

          <div className="field">
            <label>Issue Detailed Description</label>
            <textarea rows={4} value={form.issue_detail_desc}
              onChange={e=>set('issue_detail_desc',e.target.value)}
              placeholder="Full description, steps to reproduce, error messages..."/>
          </div>

          <div className="form-grid">
            <div className="field">
              <label>
                GTL Responded On
                {!isAdmin && <span style={{marginLeft:6,fontSize:10,color:'#aaa',fontWeight:400}}><i className="fa-solid fa-lock" style={{fontSize:9}}></i> admin only</span>}
              </label>
              <input type="datetime-local" value={form.gtl_responded_on}
                onChange={e=>isAdmin&&set('gtl_responded_on',e.target.value)}
                readOnly={!isAdmin}
                style={!isAdmin?{background:'#f8f9ff',color:'#aaa',cursor:'not-allowed'}:{}}/>
            </div>
            <div className="field">
              <label>Latest Update Note</label>
              <input type="text" value={form.latest_update_note}
                onChange={e=>set('latest_update_note',e.target.value)} placeholder="Short update..."/>
            </div>

            {/* Resolution & Remarks — edit mode only */}
            {isEdit && (
              <>
                <div className="field full">
                  <label>Resolution Description</label>
                  <textarea rows={2} value={form.resolution_desc}
                    onChange={e=>set('resolution_desc',e.target.value)}
                    placeholder="How was the issue resolved? (Required to mark as Resolved)"/>
                </div>
                <div className="field full">
                  <label>Remarks</label>
                  <textarea rows={2} value={form.remarks}
                    onChange={e=>set('remarks',e.target.value)} placeholder="Internal notes..."/>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Call Details — edit mode only ── */}
      {isEdit && (
        <div className="card">
          <div className="section-divider"><i className="fa-solid fa-phone"></i> Call Details</div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,minWidth:600}}>
              <thead>
                <tr style={{background:'#f8f9ff'}}>
                  {['#','R&D Start','R&D End','Call Start','Call End','Notes',''].map((h,i)=>(
                    <th key={i} style={{padding:'8px 10px',textAlign:'left',color:'#555',fontWeight:500,borderBottom:'1px solid #e8e8f0'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {calls.map((c,i)=>(
                  <tr key={i}>
                    <td style={{padding:'6px 10px',borderBottom:'1px solid #f0f0f8',color:'#888'}}>{i+1}</td>
                    {['rd_start_dt','rd_end_dt','call_start_dt','call_end_dt'].map(k=>(
                      <td key={k} style={{padding:'4px 6px',borderBottom:'1px solid #f0f0f8'}}>
                        <input type="datetime-local" value={c[k]}
                          onChange={e=>setCall(i,k,e.target.value)}
                          style={{fontSize:12,padding:'5px 7px',border:'1px solid #dde1ec',borderRadius:6,width:'100%'}}/>
                      </td>
                    ))}
                    <td style={{padding:'4px 6px',borderBottom:'1px solid #f0f0f8'}}>
                      <input type="text" value={c.call_notes}
                        onChange={e=>setCall(i,'call_notes',e.target.value)}
                        placeholder="Notes"
                        style={{fontSize:12,padding:'5px 7px',border:'1px solid #dde1ec',borderRadius:6,width:'100%'}}/>
                    </td>
                    <td style={{padding:'4px 6px',borderBottom:'1px solid #f0f0f8'}}>
                      {calls.length>1 && (
                        <button type="button" className="btn btn-sm btn-danger btn-icon"
                          onClick={()=>removeCall(i)}>
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" style={{marginTop:10}} onClick={addCall}>
            <i className="fa-solid fa-plus"></i> Add call entry
          </button>
          <p className="hint" style={{marginTop:6}}>
            <i className="fa-solid fa-circle-info"></i> Call duration is auto-calculated from start and end times.
          </p>
        </div>
      )}

      <div style={{display:'flex',justifyContent:'flex-end',gap:10,paddingBottom:20}}>
        <button type="button" className="btn btn-secondary" onClick={()=>window.history.back()}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? <><i className="fa-solid fa-spinner fa-spin"></i> Saving...</> : <><i className="fa-solid fa-check"></i> Save Ticket</>}
        </button>
      </div>
    </form>
  );
}